/**
 * Calendar-day arithmetic.
 *
 * Rent is due on a *day*, not at an instant. A lease that starts on 1 March starts on 1 March
 * for everyone, whatever timezone the server happens to run in. So every date that means "a
 * day" is carried through the whole system as a `YYYY-MM-DD` string — the `PlainDate` type
 * below — and never as a JavaScript `Date`.
 *
 * This removes, by construction, the single most common bug in property software: an invoice
 * that appears a day early or a day late for half the users.
 */

export type PlainDate = string; // YYYY-MM-DD

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isPlainDate(value: unknown): value is PlainDate {
  if (typeof value !== "string" || !DATE_RE.test(value)) return false;
  const [y, m, d] = value.split("-").map(Number);
  if (m < 1 || m > 12) return false;
  return d >= 1 && d <= daysInMonth(y, m);
}

export function assertPlainDate(value: unknown, label = "date"): PlainDate {
  if (!isPlainDate(value)) throw new Error(`Invalid ${label}: ${String(value)}`);
  return value;
}

export function daysInMonth(year: number, month1to12: number): number {
  return new Date(Date.UTC(year, month1to12, 0)).getUTCDate();
}

export function toPlainDate(d: Date): PlainDate {
  return d.toISOString().slice(0, 10);
}

/** The current day in a given IANA timezone. Defaults to UTC so that tests are deterministic. */
export function today(timeZone = "UTC", now: Date = new Date()): PlainDate {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
  return parts; // en-CA formats as YYYY-MM-DD
}

function parts(date: PlainDate): [number, number, number] {
  const [y, m, d] = date.split("-").map(Number);
  return [y, m, d];
}

function pad(n: number, width = 2): string {
  return String(n).padStart(width, "0");
}

function build(y: number, m: number, d: number): PlainDate {
  return `${pad(y, 4)}-${pad(m)}-${pad(d)}`;
}

/** Days since the epoch — the basis for all comparison and difference maths. */
export function toEpochDay(date: PlainDate): number {
  const [y, m, d] = parts(date);
  return Math.floor(Date.UTC(y, m - 1, d) / 86_400_000);
}

export function fromEpochDay(epochDay: number): PlainDate {
  const d = new Date(epochDay * 86_400_000);
  return build(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

export function addDays(date: PlainDate, days: number): PlainDate {
  return fromEpochDay(toEpochDay(date) + days);
}

/**
 * Add months, clamping the day to the end of the target month.
 *
 * 31 January + 1 month is 28 February (or 29 in a leap year), not 3 March. Every rent cycle in
 * the product goes through here, so a tenancy that starts on the 31st bills on the last day of
 * every short month and stays on the 31st in long ones.
 */
export function addMonths(date: PlainDate, months: number): PlainDate {
  const [y, m, d] = parts(date);
  const total = y * 12 + (m - 1) + months;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return build(ny, nm, Math.min(d, daysInMonth(ny, nm)));
}

export function addYears(date: PlainDate, years: number): PlainDate {
  return addMonths(date, years * 12);
}

export function diffDays(from: PlainDate, to: PlainDate): number {
  return toEpochDay(to) - toEpochDay(from);
}

export function compareDates(a: PlainDate, b: PlainDate): number {
  return a < b ? -1 : a > b ? 1 : 0; // ISO dates sort correctly as strings
}

export const minDate = (a: PlainDate, b: PlainDate): PlainDate => (a <= b ? a : b);
export const maxDate = (a: PlainDate, b: PlainDate): PlainDate => (a >= b ? a : b);

export function isBefore(a: PlainDate, b: PlainDate): boolean {
  return a < b;
}
export function isAfter(a: PlainDate, b: PlainDate): boolean {
  return a > b;
}
export function isBetween(date: PlainDate, start: PlainDate, end: PlainDate): boolean {
  return date >= start && date <= end;
}

export function startOfMonth(date: PlainDate): PlainDate {
  const [y, m] = parts(date);
  return build(y, m, 1);
}

export function endOfMonth(date: PlainDate): PlainDate {
  const [y, m] = parts(date);
  return build(y, m, daysInMonth(y, m));
}

/** Inclusive count of days in a period — 1 Jan to 31 Jan is 31 days, not 30. */
export function inclusiveDays(start: PlainDate, end: PlainDate): number {
  return diffDays(start, end) + 1;
}

/** `2026-08-19` to `19 Aug 2026`. The product never shows an ambiguous numeric date. */
export function formatDate(date: PlainDate, opts: { long?: boolean } = {}): string {
  const [y, m, d] = parts(date);
  const months = opts.long
    ? ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
    : ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${d} ${months[m - 1]} ${y}`;
}

/** "in 12 days", "today", "9 days ago" — the phrasing the dashboard and reminders use. */
export function relativeDays(from: PlainDate, to: PlainDate): string {
  const n = diffDays(from, to);
  if (n === 0) return "today";
  if (n === 1) return "tomorrow";
  if (n === -1) return "yesterday";
  if (n > 0) return `in ${n} days`;
  return `${Math.abs(n)} days ago`;
}

/** Month key `YYYY-MM`, used for grouping collections and usage counters. */
export function monthKey(date: PlainDate): string {
  return date.slice(0, 7);
}

export function monthLabel(key: string): string {
  const [y, m] = key.split("-").map(Number);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[m - 1]} ${y}`;
}

/** The last `count` month keys ending with the month containing `date`, oldest first. */
export function lastMonthKeys(date: PlainDate, count: number): string[] {
  const out: string[] = [];
  for (let i = count - 1; i >= 0; i--) out.push(monthKey(addMonths(startOfMonth(date), -i)));
  return out;
}
